import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/refund")({
  head: () => ({
    meta: [
      { title: "Refund Policy — Lumvio" },
      { name: "description", content: "How refunds, disputes, and protected payment releases work on Lumvio." },
      { property: "og:title", content: "Refund Policy — Lumvio" },
      { property: "og:description", content: "How refunds and payment protection disputes work on Lumvio." },
      { property: "og:url", content: "https://lumvio.co/refund" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/refund" }],
  }),
  component: RefundPage,
});

function RefundPage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");
  return (
    <LegalPageLayout title={ar ? "سياسة الاسترداد" : "Refund Policy"} updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}>
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        This Refund Policy explains how funds held under payment protection on Lumvio are released, refunded, or
        disputed. It should be read together with our Terms of Service.
      </p>

      <h2>1. Payment Protection Model</h2>
      <p>
        Clients fund each milestone into payment protection before the freelancer begins work. Funds are
        released to the freelancer only after the client approves the delivered work, or after an
        automatic release period lapses without objection.
      </p>

      <h2>2. Refunds Before Work Starts</h2>
      <p>
        If a freelancer has not started work on a milestone and both parties agree to cancel, the
        client receives a full refund of the protected amount, minus any non-refundable payment
        processing fees.
      </p>

      <h2>3. Refunds After Work Has Started</h2>
      <p>
        Once work has begun, refunds are handled through the dispute process. Parties are encouraged
        to negotiate a partial release reflecting the work actually completed.
      </p>

      <h2>4. Disputes</h2>
      <ul>
        <li>Either party may open a dispute from the project workspace within 14 days of delivery.</li>
        <li>Both parties are asked to submit evidence: the brief, deliverables, messages, and files.</li>
        <li>Lumvio reviews the record and may release, refund, or split the protected amount.</li>
        <li>Dispute decisions are binding for the release of protected funds only and do not limit legal remedies.</li>
      </ul>

      <h2>5. Non-Refundable Items</h2>
      <ul>
        <li>Platform service fees on completed and approved milestones.</li>
        <li>Payment processing fees charged by third-party providers.</li>
        <li>Subscription fees for elapsed subscription periods.</li>
      </ul>

      <h2>6. Chargebacks</h2>
      <p>
        Initiating a chargeback with your card issuer instead of using the dispute process may
        result in account suspension. Please contact us first — we will always try to resolve issues
        internally.
      </p>

      <h2>7. Processing Time</h2>
      <p>
        Approved refunds are returned to the original payment method. Depending on your bank or card
        issuer, the refund may take 5–10 business days to appear.
      </p>

      <h2>8. Contact</h2>
      <p>
        For refund questions, email <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        توضح سياسة الاسترداد هذه كيف يتم الإفراج عن الأموال المحمية على Lumvio أو استردادها أو
        الاعتراض عليها. ينبغي قراءتها إلى جانب شروط الخدمة الخاصة بنا.
      </p>

      <h2>1. نموذج حماية المدفوعات</h2>
      <p>
        يقوم العملاء بتمويل كل مرحلة في نظام حماية المدفوعات قبل بدء المستقل بالعمل. لا يُفرَج عن الأموال إلى المستقل إلا بعد
        موافقة العميل على العمل المُسلَّم، أو بعد انقضاء فترة الإفراج التلقائي دون اعتراض.
      </p>

      <h2>2. الاسترداد قبل بدء العمل</h2>
      <p>
        إذا لم يبدأ المستقل العمل في المرحلة واتفق الطرفان على الإلغاء، يحصل العميل على استرداد كامل للمبلغ المودع في
        حماية المدفوعات، مطروحًا منه أي رسوم معالجة دفع غير قابلة للاسترداد.
      </p>

      <h2>3. الاسترداد بعد بدء العمل</h2>
      <p>
        بمجرد بدء العمل، تُعالَج طلبات الاسترداد عبر مسار النزاع. يُشجَّع الطرفان على التفاوض حول إفراج جزئي يعكس العمل
        المُنجَز فعليًا.
      </p>

      <h2>4. النزاعات</h2>
      <ul>
        <li>يجوز لأي طرف فتح نزاع من مساحة عمل المشروع خلال 14 يومًا من التسليم.</li>
        <li>يُطلب من الطرفين تقديم الأدلة: الملخص، المخرجات، الرسائل، والملفات.</li>
        <li>تراجع Lumvio السجل وقد تُفرج عن المبلغ أو تسترده أو تقسّمه.</li>
        <li>قرارات النزاع ملزمة فيما يتعلق بالإفراج عن الأموال المحمية فقط ولا تحدّ من سبل الانتصاف القانونية.</li>
      </ul>

      <h2>5. البنود غير القابلة للاسترداد</h2>
      <ul>
        <li>رسوم خدمة المنصة على المراحل المكتملة والمعتمدة.</li>
        <li>رسوم معالجة الدفع التي يتقاضاها مزوّدو الخدمة الخارجيون.</li>
        <li>رسوم الاشتراك عن فترات الاشتراك المنقضية.</li>
      </ul>

      <h2>6. عمليات ردّ المبالغ المدفوعة (Chargebacks)</h2>
      <p>
        قد يؤدي بدء إجراء ردّ المبلغ لدى مُصدر بطاقتك بدلاً من استخدام مسار النزاع إلى تعليق الحساب. يرجى التواصل معنا
        أولاً — سنسعى دائمًا لحل المشكلات داخليًا.
      </p>

      <h2>7. مدة المعالجة</h2>
      <p>
        تُعاد المبالغ المستردة المعتمدة إلى طريقة الدفع الأصلية. وفقًا لمصرفك أو مُصدر بطاقتك، قد يستغرق ظهور المبلغ من
        5 إلى 10 أيام عمل.
      </p>

      <h2>8. التواصل</h2>
      <p>
        لأسئلة الاسترداد، راسل <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
      </p>
    </>
  );
}
